export type SupportedLocale = "nl" | "fr" | "en";

export const DEFAULT_LOCALE: SupportedLocale = "nl";

export function isSupportedLocale(lang: string): lang is SupportedLocale {
  return lang === "nl" || lang === "fr" || lang === "en";
}

export function detectBrowserLocale(): SupportedLocale {
  const langs =
    typeof navigator !== "undefined" ? (navigator.languages ?? [navigator.language]) : [];
  for (const raw of langs) {
    if (!raw) continue;
    const code = raw.split("-")[0]?.toLowerCase();
    if (code && isSupportedLocale(code)) return code;
  }
  return DEFAULT_LOCALE;
}

export function toBcp47(locale: SupportedLocale): string {
  return locale === "fr" ? "fr-BE" : locale === "en" ? "en-GB" : "nl-BE";
}

type TranslationTable = Record<string, string>;

const nl: TranslationTable = {
  // ── vehicle-stock-card ─────────────────────────────────
  "vehicleCard.searching": "Zoeken in de catalogus\u2026",
  "vehicleCard.loadingDetails": "Details opvragen\u2026",
  "vehicleCard.noResults": "Geen resultaten",
  "vehicleCard.tellMeMore": "Vertel me meer over de {name}.",
  "vehicleCard.from": "vanaf ",
  "vehicleCard.motorization": "motorisatie",
  "vehicleCard.motorizations": "motorisaties",
  "vehicleCard.notFound": "Ik kon dit model niet vinden. Wilt u op merk zoeken?",
  "vehicleCard.chooseMotorization": "Kies een motorisatie",
  "vehicleCard.bookTestDrive": "Boek een proefrit",
  "vehicleCard.planShowroom": "Plan een showroombezoek",
  "vehicleCard.busy": "Bezig\u2026",
  "vehicleCard.hp": "pk",
  "vehicleCard.watchVideo": "Bekijk de video",
  "vehicleCard.couldNotUpdate": "Could not update context.",
  "vehicleCard.filterBrand": "Merk",
  "vehicleCard.filterFuel": "Brandstof",
  "vehicleCard.filterMaxPrice": "Max",
  "vehicleCard.resultsCount": "{count} model(len)",
  "vehicleCard.viewDetails": "Bekijk details",
  "vehicleCard.whyThisFits": "Waarom dit bij u past:",
  "vehicleCard.moreResultsHidden": "+{count} meer — verfijn uw filters om te beperken",
  "vehicleCard.showMore": "Toon {count} meer\u2026",
  "vehicleCard.showLess": "Toon minder",

  // ── lease-calculator-card ──────────────────────────────
  "leaseCalc.label": "Leaseberekening",
  "leaseCalc.term": "Leasetermijn",
  "leaseCalc.downPaymentLabel": "Voorschot: {amount}",
  "leaseCalc.estimated": "Geschatte maandelijkse betaling",
  "leaseCalc.perMonth": "/maand",
  "leaseCalc.fuelSavings": "Bespaar ~{amount}/jaar op brandstof",
  "leaseCalc.calculating": "Leaseberekening\u2026",

  // ── financing-eligibility-card ─────────────────────────
  "financing.label": "Financieringsschatter",
  "financing.progress": "Vraag {n} van {total}",
  "financing.q1": "Hoeveel jaar woont u al op uw huidige adres?",
  "financing.q1.hint": "Voer het aantal jaren in",
  "financing.q2": "Is uw maandelijkse huur/hypotheek minder dan 1/3 van uw inkomen?",
  "financing.q3": "Bent u meer dan 12 maanden bij uw huidige werkgever?",
  "financing.q4": "Spaart u elke maand een vast bedrag?",
  "financing.q5": "Heeft u de afgelopen 3 jaar onbetaalde rekeningen naar de incasso gehad?",
  "financing.q6": "Wat is uw geschatte maandelijkse gezinsinkomen?",
  "financing.q7": "Hoeveel personen ten laste heeft u?",
  "financing.q7.hint": "Voer het aantal in",
  "financing.yes": "Ja",
  "financing.no": "Nee",
  "financing.next": "Volgende",
  "financing.back": "Vorige",
  "financing.hero": "U komt in aanmerking voor een wagen tot",
  "financing.hero.sub": "Op basis van uw financi\u00EBle profiel",
  "financing.options.label": "Geschatte maandelijkse betalingsopties:",
  "financing.optionPopular": "Populair",
  "financing.perMonth": "/mnd",
  "financing.disclaimer":
    "Let op: Dit is slechts een schatting. De uiteindelijke goedkeuring hangt af van kredietcontrole en eisen van de kredietverstrekker. De getoonde rente is 5% JKP ter illustratie.",
  "financing.monthsSuffix": "maanden",
  "financing.incomeBand.1500-2500": "\u20AC1.500 - \u20AC2.500",
  "financing.incomeBand.2500-3500": "\u20AC2.500 - \u20AC3.500",
  "financing.incomeBand.3500-5000": "\u20AC3.500 - \u20AC5.000",
  "financing.incomeBand.5500-8500": "\u20AC5.500 - \u20AC8.500",

  // ── slot-picker ────────────────────────────────────────
  "slotPicker.testDrive": "proefrit",
  "slotPicker.showroomVisit": "showroombezoek",
  "slotPicker.heading": "Kies een moment voor je {acceptanceLabel}",
  "slotPicker.forVehicle": "Voor de {vehicle}",
  "slotPicker.sendError": "Kon niet naar de chat sturen. Probeer het opnieuw.",
  "slotPicker.retryError": "Selecteer opnieuw of probeer later.",
  "slotPicker.selectDate": "Kies een datum",
  "slotPicker.availableSlots": "Beschikbare tijden",
  "slotPicker.synthWarning": "Onder voorbehoud van bevestiging door de dealer",
  "slotPicker.nameLabel": "Naam",
  "slotPicker.namePlaceholder": "Uw naam",
  "slotPicker.emailLabel": "E-mail",
  "slotPicker.emailPlaceholder": "E-mail",
  "slotPicker.submitCta": "Boek tijdslot",
  "slotPicker.submitting": "Verzenden\u2026",
  "slotPicker.submitted": "Verzonden",

  // ── csat-rating ────────────────────────────────────────
  "csatRating.label": "Hoe beoordeelt u uw ervaring?",
  "csatRating.hint": "1 = slecht, 5 = uitstekend",
  "csatRating.aria.rate": "Geef een score van {n} op 5",
  "csatRating.submitted": "Bedankt voor uw beoordeling!",

  // ── booking-confirmation-card ──────────────────────────
  "booking.testDrive": "Proefrit",
  "booking.showroomVisit": "Showroombezoek",
  "booking.requested": "{type} aangevraagd",
  "booking.confirmed": "{type} bevestigd",
  "booking.pendingNote":
    "De dealer belt u binnen 24u terug om de afspraak definitief te bevestigen.",
  "booking.reference": "Referentie",
  "booking.when": "Wanneer",
  "booking.where": "Waar",
  "booking.timeConnector": "om",

  // ── thread ─────────────────────────────────────────────
  "thread.greeting":
    "Hallo! Ik ben je Northway Motors showroomassistent. Ik kan je helpen met actuele aanbiedingen, voertuigen op voorraad, een proefrit boeken, of de dichtstbijzijnde vestiging. Hoe kan ik je helpen?",
  "thread.quickAction.testDrive": "Boek een proefrit",
  "thread.quickAction.testDrive.prompt": "Ik wil graag een proefrit boeken.",
  "thread.quickAction.offers": "Actuele aanbiedingen",
  "thread.quickAction.offers.prompt": "Laat me de huidige aanbiedingen zien.",
  "thread.quickAction.showroom": "Bezoek een showroom",
  "thread.quickAction.showroom.prompt": "Ik wil graag een showroom bezoeken.",
  "thread.composer.placeholder": "Stel een vraag",
  "thread.composer.ariaLabel": "Berichtinvoer",
  "thread.composer.voice": "Spraakinvoer",
  "thread.composer.stopVoice": "Stop spraakinvoer",
  "thread.composer.voiceUnsupported": "Spraakinvoer wordt niet ondersteund in deze browser",
  "thread.composer.voicePermissionDenied":
    "Microfoontoegang is geblokkeerd. Sta microfoonrechten toe in uw browserinstellingen voor deze site.",
  "thread.composer.voiceRequiresSecureContext":
    "Spraakinvoer werkt alleen op een beveiligde pagina (HTTPS of localhost).",
  "thread.composer.voiceUnavailable":
    "Spraakinvoer kon niet worden gestart. Controleer microfoonrechten en probeer opnieuw.",
  "thread.composer.send": "Verstuur bericht",
  "thread.composer.stop": "Stop met genereren",
  "thread.voice.start": "Opname starten",
  "thread.voice.stop": "Opname stoppen",
  "thread.voice.error": "Kan microfoon niet gebruiken",
  "thread.voice.listening": "Luisteren\u2026",
  "thread.voice.transcribing": "Transcriberen\u2026",
  "thread.voice.play": "Bericht voorlezen",
  "thread.voice.stopPlayback": "Voorlezen stoppen",
  "thread.voice.autoReadOn": "Automatisch voorlezen: aan",
  "thread.voice.autoReadOff": "Automatisch voorlezen: uit",
  "thread.voice.autoListenOn": "Automatisch luisteren: aan",
  "thread.voice.autoListenOff": "Automatisch luisteren: uit",
  "thread.action.copy": "Kopieer",
  "thread.action.edit": "Bewerk",
  "thread.action.previous": "Vorige",
  "thread.action.next": "Volgende",
  "thread.edit.cancel": "Annuleer",
  "thread.edit.update": "Bijwerken",

  // ── chat-widget ────────────────────────────────────────
  "widget.ariaLabel": "Northway showroomassistent",
  "widget.title": "Northway Assistant",
  "widget.subtitle": "Showroomassistent",
  "widget.close": "Chat sluiten",
  "widget.startNew": "Nieuw gesprek starten",
  "widget.openAriaLabel": "Maak een afspraak",
  "widget.openButton": "Maak een afspraak",
  "widget.agentAlt": "Chat agent",

  // ── dealer-location-tool-card ──────────────────────────
  "dealer.searching": "Vestigingen zoeken\u2026",
  "dealer.brandNotFound": "Merk niet gevonden",
  "dealer.brandNotFoundHint": "Kies een van de beschikbare merken hieronder.",
  "dealer.resultsFor": "Resultaten voor {query}",
  "dealer.branches": "{brand} vestigingen",
  "dealer.lookupFailed": "Lookup mislukt",
  "dealer.tryAgain": "Probeer het opnieuw.",
  "dealer.noLocations": "Geen vestigingen gevonden",
  "dealer.noLocationsHint": "Probeer een andere stad, postcode of dealernaam.",
  "dealer.nearestTitle": "Dichtstbijzijnde vestigingen",
  "dealer.listTitle": "Vestigingen",
  "dealer.locationAvailable": "Locatie beschikbaar",
  "dealer.hoursUnavailable": "Openingsuren niet beschikbaar",
  "dealer.chooseDealer": "Kies deze vestiging",
  "dealer.chosen": "Gekozen",
  "dealer.route": "Route",
  "dealer.couldNotSave": "Kon de keuze niet opslaan.",

  // ── tool-fallback ──────────────────────────────────────
  "tool.IdentifyCustomer.running": "Uw profiel opzoeken\u2026",
  "tool.IdentifyCustomer.done": "Klant ge\u00EFdentificeerd",
  "tool.ProposeSlots.running": "Beschikbare tijdstippen zoeken\u2026",
  "tool.ProposeSlots.done": "Tijdstippen klaar",
  "tool.ConfirmBooking.running": "Uw boeking bevestigen\u2026",
  "tool.ConfirmBooking.done": "Boeking bevestigd",
  "tool.ClassifyCustomerTypology.running": "Uw voorkeuren analyseren\u2026",
  "tool.ClassifyCustomerTypology.done": "Voorkeuren geanalyseerd",
  "tool.FindDealerLocations.running": "Vestigingen in de buurt zoeken\u2026",
  "tool.FindDealerLocations.done": "Vestigingen gevonden",
  "tool.ListDealerLocations.running": "Vestigingen ophalen\u2026",
  "tool.ListDealerLocations.done": "Vestigingen opgehaald",
  "tool.SearchVehicleStock.running": "Voertuigen zoeken\u2026",
  "tool.SearchVehicleStock.done": "Voertuigen gevonden",
  "tool.GetVehicleStockDetails.running": "Voertuigdetails ophalen\u2026",
  "tool.GetVehicleStockDetails.done": "Voertuigdetails klaar",
  "tool.genericRunning": "{toolName} wordt uitgevoerd\u2026",
  "tool.genericDone": "{toolName} voltooid",
  "tool.cancelled": "Geannuleerd: {label}",
  "tool.cancelledTool": "Geannuleerd: {toolName}",
  "tool.result": "Resultaat:",
  "tool.cancelledReason": "Reden annulering:",
  "tool.error": "Fout:",

  // ── side-panel ──────────────────────────────────────────
  "sidePanel.title": "Voertuigen",
  "sidePanel.close": "Paneel sluiten",
  "sidePanel.backToChat": "Terug naar chat",
  "sidePanel.foundVehicles": "{count} voertuig(en) gevonden",
  "sidePanel.viewResults": "Bekijk resultaten",
  "sidePanel.viewDetails": "Bekijk details",
  "sidePanel.vehicleDetails": "Voertuigdetails",

  // ── markdown-text ──────────────────────────────────────
  "markdown.copy": "Kopieer",

  // ── runtime ────────────────────────────────────────────
  "runtime.interrupted": "Het antwoord werd onderbroken. Probeer het opnieuw.",
};

const fr: TranslationTable = {
  // ── vehicle-stock-card ─────────────────────────────────
  "vehicleCard.searching": "Recherche dans le catalogue\u2026",
  "vehicleCard.loadingDetails": "Chargement des d\u00E9tails\u2026",
  "vehicleCard.noResults": "Aucun r\u00E9sultat",
  "vehicleCard.tellMeMore": "Parlez-moi de la {name}.",
  "vehicleCard.from": "\u00E0 partir de ",
  "vehicleCard.motorization": "motorisation",
  "vehicleCard.motorizations": "motorisations",
  "vehicleCard.notFound":
    "Je n\u2019ai pas trouv\u00E9 ce mod\u00E8le. Voulez-vous chercher par marque\u00A0?",
  "vehicleCard.chooseMotorization": "Choisissez une motorisation",
  "vehicleCard.bookTestDrive": "R\u00E9server un essai routier",
  "vehicleCard.planShowroom": "Planifier une visite en showroom",
  "vehicleCard.busy": "En cours\u2026",
  "vehicleCard.hp": "ch",
  "vehicleCard.watchVideo": "Voir la vid\u00E9o",
  "vehicleCard.couldNotUpdate": "Impossible de mettre \u00E0 jour le contexte.",
  "vehicleCard.filterBrand": "Marque",
  "vehicleCard.filterFuel": "Carburant",
  "vehicleCard.filterMaxPrice": "Max",
  "vehicleCard.resultsCount": "{count} mod\u00E8le(s)",
  "vehicleCard.viewDetails": "Voir les d\u00E9tails",
  "vehicleCard.whyThisFits": "Pourquoi ce mod\u00E8le vous convient\u00A0:",
  "vehicleCard.moreResultsHidden": "+{count} de plus \u2014 affinez vos filtres pour r\u00E9duire",
  "vehicleCard.showMore": "Afficher {count} de plus\u2026",
  "vehicleCard.showLess": "Afficher moins",

  // ── lease-calculator-card ──────────────────────────────
  "leaseCalc.label": "Calcul de leasing",
  "leaseCalc.term": "Dur\u00E9e du leasing",
  "leaseCalc.downPaymentLabel": "Acompte\u00A0: {amount}",
  "leaseCalc.estimated": "Mensualit\u00E9 estim\u00E9e",
  "leaseCalc.perMonth": "/mois",
  "leaseCalc.fuelSavings": "\u00C9conomisez ~{amount}/an en carburant",
  "leaseCalc.calculating": "Calcul du leasing\u2026",

  // ── financing-eligibility-card ─────────────────────────
  "financing.label": "Estimateur d'\u00E9ligibilit\u00E9 au financement",
  "financing.progress": "Question {n} sur {total}",
  "financing.q1": "Depuis combien d'ann\u00E9es vivez-vous \u00E0 votre adresse actuelle\u00A0?",
  "financing.q1.hint": "Entrez le nombre d'ann\u00E9es",
  "financing.q2":
    "Votre loyer/hypoth\u00E8que mensuel(le) est-il inf\u00E9rieur \u00E0 1/3 de vos revenus\u00A0?",
  "financing.q3": "\u00CAtes-vous chez votre employeur actuel depuis plus de 12 mois\u00A0?",
  "financing.q4": "\u00C9pargnez-vous un montant fixe chaque mois\u00A0?",
  "financing.q5":
    "Avez-vous eu des factures impay\u00E9es envoy\u00E9es en recouvrement ces 3 derni\u00E8res ann\u00E9es\u00A0?",
  "financing.q6": "Quel est votre revenu mensuel estim\u00E9 du m\u00E9nage\u00A0?",
  "financing.q7": "Combien de personnes \u00E0 charge avez-vous\u00A0?",
  "financing.q7.hint": "Entrez le nombre",
  "financing.yes": "Oui",
  "financing.no": "Non",
  "financing.next": "Suivant",
  "financing.back": "Retour",
  "financing.hero": "Vous pouvez financer une voiture jusqu'\u00E0",
  "financing.hero.sub": "Sur la base de votre profil financier",
  "financing.options.label": "Options de paiement mensuel estim\u00E9es\u00A0:",
  "financing.optionPopular": "Populaire",
  "financing.perMonth": "/mois",
  "financing.disclaimer":
    "Note\u00A0: Il s'agit d'une estimation. L'approbation finale d\u00E9pend du contr\u00F4le de cr\u00E9dit et des exigences du pr\u00EAteur. Le taux de 5% TAEG est \u00E0 titre indicatif.",
  "financing.monthsSuffix": "mois",
  "financing.incomeBand.1500-2500": "\u20AC1\u202F500 - \u20AC2\u202F500",
  "financing.incomeBand.2500-3500": "\u20AC2\u202F500 - \u20AC3\u202F500",
  "financing.incomeBand.3500-5000": "\u20AC3\u202F500 - \u20AC5\u202F000",
  "financing.incomeBand.5500-8500": "\u20AC5\u202F500 - \u20AC8\u202F500",

  // ── slot-picker ────────────────────────────────────────
  "slotPicker.testDrive": "essai routier",
  "slotPicker.showroomVisit": "visite en showroom",
  "slotPicker.heading": "Choisissez un moment pour votre {acceptanceLabel}",
  "slotPicker.forVehicle": "Pour la {vehicle}",
  "slotPicker.sendError": "Impossible d\u2019envoyer au chat. Veuillez r\u00E9essayer.",
  "slotPicker.retryError": "S\u00E9lectionnez \u00E0 nouveau ou r\u00E9essayez plus tard.",
  "slotPicker.selectDate": "Choisissez une date",
  "slotPicker.nameLabel": "Nom",
  "slotPicker.namePlaceholder": "Votre nom",
  "slotPicker.emailLabel": "E-mail",
  "slotPicker.emailPlaceholder": "E-mail",
  "slotPicker.submitCta": "R\u00E9server le cr\u00E9neau",
  "slotPicker.submitting": "Envoi\u2026",
  "slotPicker.submitted": "Envoy\u00E9",
  "slotPicker.availableSlots": "Cr\u00E9neaux disponibles",
  "slotPicker.synthWarning": "Sous r\u00E9serve de confirmation par le concessionnaire",

  // ── csat-rating ────────────────────────────────────────
  "csatRating.label": "Comment \u00E9valuez-vous votre exp\u00E9rience\u00A0?",
  "csatRating.hint": "1 = mauvais, 5 = excellent",
  "csatRating.aria.rate": "Donner une note de {n} sur 5",
  "csatRating.submitted": "Merci pour votre \u00E9valuation\u00A0!",

  // ── booking-confirmation-card ──────────────────────────
  "booking.testDrive": "Essai routier",
  "booking.showroomVisit": "Visite en showroom",
  "booking.requested": "{type} demand\u00E9(e)",
  "booking.confirmed": "{type} confirm\u00E9(e)",
  "booking.pendingNote":
    "Le concessionnaire vous rappellera dans les 24h pour confirmer le rendez-vous.",
  "booking.reference": "R\u00E9f\u00E9rence",
  "booking.when": "Quand",
  "booking.where": "O\u00F9",
  "booking.timeConnector": "\u00E0",

  // ── thread ─────────────────────────────────────────────
  "thread.greeting":
    "Bonjour\u00A0! Je suis votre assistant showroom Northway Motors. Je peux vous aider \u00E0 trouver les offres actuelles, les v\u00E9hicules en stock, r\u00E9server un essai routier ou trouver le showroom le plus proche. Comment puis-je vous aider\u00A0?",
  "thread.quickAction.testDrive": "R\u00E9server un essai",
  "thread.quickAction.testDrive.prompt": "J\u2019aimerais r\u00E9server un essai routier.",
  "thread.quickAction.offers": "Offres actuelles",
  "thread.quickAction.offers.prompt": "Montrez-moi les offres actuelles.",
  "thread.quickAction.showroom": "Visiter un showroom",
  "thread.quickAction.showroom.prompt": "J\u2019aimerais visiter un showroom.",
  "thread.composer.placeholder": "Posez une question",
  "thread.composer.ariaLabel": "Saisie du message",
  "thread.composer.voice": "Entr\u00E9e vocale",
  "thread.composer.stopVoice": "Arr\u00EAter l\u2019entr\u00E9e vocale",
  "thread.composer.voiceUnsupported":
    "L\u2019entr\u00E9e vocale n\u2019est pas prise en charge dans ce navigateur",
  "thread.composer.voicePermissionDenied":
    "L\u2019acc\u00E8s au microphone est bloqu\u00E9. Autorisez le microphone pour ce site dans les param\u00E8tres du navigateur.",
  "thread.composer.voiceRequiresSecureContext":
    "L\u2019entr\u00E9e vocale fonctionne uniquement sur une page s\u00E9curis\u00E9e (HTTPS ou localhost).",
  "thread.composer.voiceUnavailable":
    "Impossible de d\u00E9marrer l\u2019entr\u00E9e vocale. V\u00E9rifiez les autorisations du microphone et r\u00E9essayez.",
  "thread.composer.send": "Envoyer le message",
  "thread.composer.stop": "Arr\u00EAter la g\u00E9n\u00E9ration",
  "thread.voice.start": "D\u00E9marrer l\u2019enregistrement",
  "thread.voice.stop": "Arr\u00EAter l\u2019enregistrement",
  "thread.voice.error": "Impossible d\u2019utiliser le microphone",
  "thread.voice.listening": "\u00C9coute\u2026",
  "thread.voice.transcribing": "Transcription\u2026",
  "thread.voice.play": "Lire le message",
  "thread.voice.stopPlayback": "Arr\u00EAter la lecture",
  "thread.voice.autoReadOn": "Lecture automatique\u00A0: activ\u00E9e",
  "thread.voice.autoReadOff": "Lecture automatique\u00A0: d\u00E9sactiv\u00E9e",
  "thread.voice.autoListenOn": "\u00C9coute auto\u00A0: activ\u00E9e",
  "thread.voice.autoListenOff": "\u00C9coute auto\u00A0: d\u00E9sactiv\u00E9e",
  "thread.action.copy": "Copier",
  "thread.action.edit": "Modifier",
  "thread.action.previous": "Pr\u00E9c\u00E9dent",
  "thread.action.next": "Suivant",
  "thread.edit.cancel": "Annuler",
  "thread.edit.update": "Mettre \u00E0 jour",

  // ── chat-widget ────────────────────────────────────────
  "widget.ariaLabel": "Assistant showroom Northway",
  "widget.title": "Northway Assistant",
  "widget.subtitle": "Assistant showroom",
  "widget.close": "Fermer le chat",
  "widget.startNew": "D\u00E9marrer une nouvelle conversation",
  "widget.openAriaLabel": "Prendre rendez-vous",
  "widget.openButton": "Prendre rendez-vous",
  "widget.agentAlt": "Agent de chat",

  // ── dealer-location-tool-card ──────────────────────────
  "dealer.searching": "Recherche de concessions\u2026",
  "dealer.brandNotFound": "Marque introuvable",
  "dealer.brandNotFoundHint": "Choisissez parmi les marques disponibles ci-dessous.",
  "dealer.resultsFor": "R\u00E9sultats pour {query}",
  "dealer.branches": "Concessions {brand}",
  "dealer.lookupFailed": "La recherche a \u00E9chou\u00E9",
  "dealer.tryAgain": "Veuillez r\u00E9essayer.",
  "dealer.noLocations": "Aucune concession trouv\u00E9e",
  "dealer.noLocationsHint": "Essayez une autre ville, code postal ou nom de concessionnaire.",
  "dealer.nearestTitle": "Concessions les plus proches",
  "dealer.listTitle": "Concessions",
  "dealer.locationAvailable": "Emplacement disponible",
  "dealer.hoursUnavailable": "Heures d\u2019ouverture non disponibles",
  "dealer.chooseDealer": "Choisir cette concession",
  "dealer.chosen": "Choisi",
  "dealer.route": "Itin\u00E9raire",
  "dealer.couldNotSave": "Impossible d\u2019enregistrer le choix.",

  // ── tool-fallback ──────────────────────────────────────
  "tool.IdentifyCustomer.running": "Recherche de votre profil\u2026",
  "tool.IdentifyCustomer.done": "Client identifi\u00E9",
  "tool.ProposeSlots.running": "Recherche de cr\u00E9neaux disponibles\u2026",
  "tool.ProposeSlots.done": "Cr\u00E9neaux pr\u00EAts",
  "tool.ConfirmBooking.running": "Confirmation de votre r\u00E9servation\u2026",
  "tool.ConfirmBooking.done": "R\u00E9servation confirm\u00E9e",
  "tool.ClassifyCustomerTypology.running": "Analyse de vos pr\u00E9f\u00E9rences\u2026",
  "tool.ClassifyCustomerTypology.done": "Pr\u00E9f\u00E9rences analys\u00E9es",
  "tool.FindDealerLocations.running": "Recherche de concessions \u00E0 proximit\u00E9\u2026",
  "tool.FindDealerLocations.done": "Concessions trouv\u00E9es",
  "tool.ListDealerLocations.running": "Chargement des concessions\u2026",
  "tool.ListDealerLocations.done": "Concessions charg\u00E9es",
  "tool.SearchVehicleStock.running": "Recherche de v\u00E9hicules\u2026",
  "tool.SearchVehicleStock.done": "V\u00E9hicules trouv\u00E9s",
  "tool.GetVehicleStockDetails.running": "Chargement des d\u00E9tails du v\u00E9hicule\u2026",
  "tool.GetVehicleStockDetails.done": "D\u00E9tails du v\u00E9hicule pr\u00EAts",
  "tool.genericRunning": "{toolName} en cours\u2026",
  "tool.genericDone": "{toolName} termin\u00E9",
  "tool.cancelled": "Annul\u00E9\u00A0: {label}",
  "tool.cancelledTool": "Annul\u00E9\u00A0: {toolName}",
  "tool.result": "R\u00E9sultat\u00A0:",
  "tool.cancelledReason": "Raison de l\u2019annulation\u00A0:",
  "tool.error": "Erreur\u00A0:",

  // ── side-panel ──────────────────────────────────────────
  "sidePanel.title": "V\u00E9hicules",
  "sidePanel.close": "Fermer le panneau",
  "sidePanel.backToChat": "Retour au chat",
  "sidePanel.foundVehicles": "{count} v\u00E9hicule(s) trouv\u00E9(s)",
  "sidePanel.viewResults": "Voir les r\u00E9sultats",
  "sidePanel.viewDetails": "Voir les d\u00E9tails",
  "sidePanel.vehicleDetails": "D\u00E9tails du v\u00E9hicule",

  // ── markdown-text ──────────────────────────────────────
  "markdown.copy": "Copier",

  // ── runtime ────────────────────────────────────────────
  "runtime.interrupted": "La r\u00E9ponse a \u00E9t\u00E9 interrompue. Veuillez r\u00E9essayer.",
};

const en: TranslationTable = {
  // ── vehicle-stock-card ─────────────────────────────────
  "vehicleCard.searching": "Searching the catalogue\u2026",
  "vehicleCard.loadingDetails": "Loading details\u2026",
  "vehicleCard.noResults": "No results",
  "vehicleCard.tellMeMore": "Tell me more about the {name}.",
  "vehicleCard.from": "from ",
  "vehicleCard.motorization": "engine",
  "vehicleCard.motorizations": "engines",
  "vehicleCard.notFound": "I couldn\u2019t find this model. Would you like to search by brand?",
  "vehicleCard.chooseMotorization": "Choose an engine",
  "vehicleCard.bookTestDrive": "Book TestDrive",
  "vehicleCard.planShowroom": "Plan a showroom visit",
  "vehicleCard.busy": "Loading\u2026",
  "vehicleCard.hp": "hp",
  "vehicleCard.watchVideo": "Watch the video",
  "vehicleCard.couldNotUpdate": "Could not update context.",
  "vehicleCard.filterBrand": "Brand",
  "vehicleCard.filterFuel": "Fuel",
  "vehicleCard.filterMaxPrice": "Max",
  "vehicleCard.resultsCount": "{count} model(s)",
  "vehicleCard.viewDetails": "View details",
  "vehicleCard.whyThisFits": "Why this fits you:",
  "vehicleCard.moreResultsHidden": "+{count} more \u2014 refine your filters to narrow down",
  "vehicleCard.showMore": "Show {count} more\u2026",
  "vehicleCard.showLess": "Show less",

  // ── lease-calculator-card ──────────────────────────────
  "leaseCalc.label": "Lease calculator",
  "leaseCalc.term": "Lease term",
  "leaseCalc.downPaymentLabel": "Down payment: {amount}",
  "leaseCalc.estimated": "Estimated monthly payment",
  "leaseCalc.perMonth": "/month",
  "leaseCalc.fuelSavings": "Save ~{amount}/year on fuel costs",
  "leaseCalc.calculating": "Calculating lease\u2026",

  // ── financing-eligibility-card ─────────────────────────
  "financing.label": "Financing eligibility estimator",
  "financing.progress": "Question {n} of {total}",
  "financing.q1": "How many years have you lived at your current address?",
  "financing.q1.hint": "Enter number of years",
  "financing.q2": "Is your monthly rent/mortgage less than 1/3 of your income?",
  "financing.q3": "Have you been with your current employer for more than 12 months?",
  "financing.q4": "Do you save a fixed amount of money every single month?",
  "financing.q5": "Have you had any unpaid bills go to collections in the last 3 years?",
  "financing.q6": "What is your estimated monthly household income?",
  "financing.q7": "How many dependents do you have?",
  "financing.q7.hint": "Enter the number",
  "financing.yes": "Yes",
  "financing.no": "No",
  "financing.next": "Next",
  "financing.back": "Back",
  "financing.hero": "You qualify for a car up to",
  "financing.hero.sub": "Based on your financial profile",
  "financing.options.label": "Estimated monthly payment options:",
  "financing.optionPopular": "Popular",
  "financing.perMonth": "/mo",
  "financing.disclaimer":
    "Note: This is an estimate only. Final approval and terms depend on credit check and lender requirements. Interest rate shown is 5% APR for illustrative purposes.",
  "financing.monthsSuffix": "months",
  "financing.incomeBand.1500-2500": "\u20AC1,500 - \u20AC2,500",
  "financing.incomeBand.2500-3500": "\u20AC2,500 - \u20AC3,500",
  "financing.incomeBand.3500-5000": "\u20AC3,500 - \u20AC5,000",
  "financing.incomeBand.5500-8500": "\u20AC5,500 - \u20AC8,500",

  // ── slot-picker ────────────────────────────────────────
  "slotPicker.testDrive": "test drive",
  "slotPicker.showroomVisit": "showroom visit",
  "slotPicker.heading": "Choose a time for your {acceptanceLabel}",
  "slotPicker.forVehicle": "For the {vehicle}",
  "slotPicker.sendError": "Could not send to chat. Please try again.",
  "slotPicker.retryError": "Please select again or try later.",
  "slotPicker.selectDate": "Select a date",
  "slotPicker.nameLabel": "Name",
  "slotPicker.namePlaceholder": "Your name",
  "slotPicker.emailLabel": "Email",
  "slotPicker.emailPlaceholder": "Email",
  "slotPicker.submitCta": "Book Slot",
  "slotPicker.submitting": "Submitting\u2026",
  "slotPicker.submitted": "Submitted",
  "slotPicker.availableSlots": "Available times",
  "slotPicker.synthWarning": "Subject to confirmation by the dealer",

  // ── csat-rating ────────────────────────────────────────
  "csatRating.label": "How would you rate this experience?",
  "csatRating.hint": "1 = poor, 5 = excellent",
  "csatRating.aria.rate": "Rate {n} out of 5",
  "csatRating.submitted": "Thanks for your rating!",

  // ── booking-confirmation-card ──────────────────────────
  "booking.testDrive": "Test drive",
  "booking.showroomVisit": "Showroom visit",
  "booking.requested": "{type} requested",
  "booking.confirmed": "{type} confirmed",
  "booking.pendingNote": "The dealer will call you within 24 hours to confirm the appointment.",
  "booking.reference": "Reference",
  "booking.when": "When",
  "booking.where": "Where",
  "booking.timeConnector": "at",

  // ── thread ─────────────────────────────────────────────
  "thread.greeting":
    "Hi! I\u2019m your Northway Motors showroom assistant. I can help you find current offers, stock vehicles, book a test drive, or the nearest location. What can I help you with?",
  "thread.quickAction.testDrive": "Book a test drive",
  "thread.quickAction.testDrive.prompt": "I\u2019d like to book a test drive.",
  "thread.quickAction.offers": "Current offers",
  "thread.quickAction.offers.prompt": "Show me the current vehicle offers.",
  "thread.quickAction.showroom": "Visit a showroom",
  "thread.quickAction.showroom.prompt": "I\u2019d like to visit a showroom.",
  "thread.composer.placeholder": "Ask anything",
  "thread.composer.ariaLabel": "Message input",
  "thread.composer.voice": "Voice input",
  "thread.composer.stopVoice": "Stop voice input",
  "thread.composer.voiceUnsupported": "Voice input is not supported in this browser",
  "thread.composer.voicePermissionDenied":
    "Microphone access is blocked. Allow microphone permission for this site in your browser settings.",
  "thread.composer.voiceRequiresSecureContext":
    "Voice input only works on a secure page (HTTPS or localhost).",
  "thread.composer.voiceUnavailable":
    "Voice input could not start. Check microphone permissions and try again.",
  "thread.composer.send": "Send message",
  "thread.composer.stop": "Stop generating",
  "thread.voice.start": "Start recording",
  "thread.voice.stop": "Stop recording",
  "thread.voice.error": "Unable to use microphone",
  "thread.voice.listening": "Listening\u2026",
  "thread.voice.transcribing": "Transcribing\u2026",
  "thread.voice.play": "Play message",
  "thread.voice.stopPlayback": "Stop playback",
  "thread.voice.autoReadOn": "Auto-read replies: on",
  "thread.voice.autoReadOff": "Auto-read replies: off",
  "thread.voice.autoListenOn": "Auto-listen: on",
  "thread.voice.autoListenOff": "Auto-listen: off",
  "thread.action.copy": "Copy",
  "thread.action.edit": "Edit",
  "thread.action.previous": "Previous",
  "thread.action.next": "Next",
  "thread.edit.cancel": "Cancel",
  "thread.edit.update": "Update",

  // ── chat-widget ────────────────────────────────────────
  "widget.ariaLabel": "Northway showroom assistant",
  "widget.title": "Northway Assistant",
  "widget.subtitle": "Showroom assistant",
  "widget.close": "Close chat",
  "widget.startNew": "Start new conversation",
  "widget.openAriaLabel": "Make an appointment",
  "widget.openButton": "Make an appointment",
  "widget.agentAlt": "Chat agent",

  // ── dealer-location-tool-card ──────────────────────────
  "dealer.searching": "Searching for locations\u2026",
  "dealer.brandNotFound": "Brand not found",
  "dealer.brandNotFoundHint": "Choose from the available brands below.",
  "dealer.resultsFor": "Results for {query}",
  "dealer.branches": "{brand} locations",
  "dealer.lookupFailed": "Lookup failed",
  "dealer.tryAgain": "Please try again.",
  "dealer.noLocations": "No locations found",
  "dealer.noLocationsHint": "Try a different city, postal code, or dealer name.",
  "dealer.nearestTitle": "Nearest locations",
  "dealer.listTitle": "Locations",
  "dealer.locationAvailable": "Location available",
  "dealer.hoursUnavailable": "Opening hours not available",
  "dealer.chooseDealer": "Choose this location",
  "dealer.chosen": "Chosen",
  "dealer.route": "Directions",
  "dealer.couldNotSave": "Could not save the choice.",

  // ── tool-fallback ──────────────────────────────────────
  "tool.IdentifyCustomer.running": "Looking up your profile\u2026",
  "tool.IdentifyCustomer.done": "Customer identified",
  "tool.ProposeSlots.running": "Finding available time slots\u2026",
  "tool.ProposeSlots.done": "Time slots ready",
  "tool.ConfirmBooking.running": "Confirming your booking\u2026",
  "tool.ConfirmBooking.done": "Booking confirmed",
  "tool.ClassifyCustomerTypology.running": "Analysing your preferences\u2026",
  "tool.ClassifyCustomerTypology.done": "Preferences analysed",
  "tool.FindDealerLocations.running": "Finding nearby dealer locations\u2026",
  "tool.FindDealerLocations.done": "Dealer locations found",
  "tool.ListDealerLocations.running": "Listing dealer locations\u2026",
  "tool.ListDealerLocations.done": "Dealer locations listed",
  "tool.SearchVehicleStock.running": "Searching vehicles\u2026",
  "tool.SearchVehicleStock.done": "Vehicles found",
  "tool.GetVehicleStockDetails.running": "Loading vehicle details\u2026",
  "tool.GetVehicleStockDetails.done": "Vehicle details ready",
  "tool.genericRunning": "Using {toolName}\u2026",
  "tool.genericDone": "Used tool: {toolName}",
  "tool.cancelled": "Cancelled: {label}",
  "tool.cancelledTool": "Cancelled tool: {toolName}",
  "tool.result": "Result:",
  "tool.cancelledReason": "Cancelled reason:",
  "tool.error": "Error:",

  // ── side-panel ──────────────────────────────────────────
  "sidePanel.title": "Vehicles",
  "sidePanel.close": "Close panel",
  "sidePanel.backToChat": "Back to chat",
  "sidePanel.foundVehicles": "Found {count} vehicle(s)",
  "sidePanel.viewResults": "View results",
  "sidePanel.viewDetails": "View details",
  "sidePanel.vehicleDetails": "Vehicle details",

  // ── markdown-text ──────────────────────────────────────
  "markdown.copy": "Copy",

  // ── runtime ────────────────────────────────────────────
  "runtime.interrupted": "The response was interrupted. Please try again.",
};

const tables: Record<SupportedLocale, TranslationTable> = { nl, fr, en };

export function t(
  locale: SupportedLocale,
  key: string,
  params?: Record<string, string | number>,
): string {
  const raw = tables[locale]?.[key] ?? tables.en[key] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) =>
    params[k] !== undefined ? String(params[k]) : `{${k}}`,
  );
}
